import { supabase } from './supabase.js';
import { enrichListingsWithAuctionBids } from './auctionsService.js';

/** Roles partner con venta de listings en marketplace (alineado con partnerRoleDefs / capacidades). */
export const PARTNER_MARKETPLACE_SELLER_ROLE_KEYS = ['store', 'label', 'artist', 'supplier'];

/**
 * @param {Array<{ role: string, role_status?: string, is_active?: boolean }>|null|undefined} rolesRows — p. ej. partner_roles del partner
 * @returns {boolean}
 */
export function partnerHasMarketplaceSellerRoles(rolesRows) {
  const sell = new Set(PARTNER_MARKETPLACE_SELLER_ROLE_KEYS);
  return (rolesRows || []).some(
    r => sell.has(r.role) && r.role_status === 'active' && r.is_active !== false
  );
}

/** Rol tienda (`store`) activo: custodia / verificación física en tienda. */
export function partnerHasActiveStoreRole(rolesRows) {
  return (rolesRows || []).some(
    r => r.role === 'store' && r.role_status === 'active' && r.is_active !== false
  );
}

/** Rol punto de recogida DreamVinyl (`pickup`) activo. */
export function partnerHasActivePickupRole(rolesRows) {
  return (rolesRows || []).some(
    r => r.role === 'pickup' && r.role_status === 'active' && r.is_active !== false
  );
}

/** Miniatura para tablas / panel: `cover_url` o primera foto. */
export function listingThumbUrlFromRow(row) {
  if (!row) return null;
  const c = row.cover_url ?? row.coverUrl;
  if (c && String(c).trim()) return String(c).trim();
  const ph = row.photos;
  if (Array.isArray(ph) && ph.length && String(ph[0]).trim()) return String(ph[0]).trim();
  return null;
}

/**
 * Mapea una fila de `listings` (Supabase) al contrato de campos
 * que usa itemFilters.js / MarketplaceItemCards / fichas.
 */
export function mapListing(row) {
  return {
    // identidad
    id: row.id,
    seller: row.profiles?.username ?? 'unknown',
    sellerUserId: row.seller_id,
    sellerAvatar: row.profiles?.avatar_url ?? null,
    sellerIsStore: row.profiles?.is_store ?? false,
    sellerIsVerified: row.profiles?.is_verified ?? false,
    /** Claves de roles partner activos (denormalizado en profiles; solo lectura pública). */
    sellerPartnerRoleKeys: Array.isArray(row.profiles?.partner_active_roles)
      ? row.profiles.partner_active_roles
      : [],

    // contenido
    title: row.title,
    artist: row.artist,
    year: row.year ?? null,
    label: row.label ?? null,
    country: row.country ?? null,
    genre: row.genre ?? null,
    condition: row.condition,
    price: Number(row.price),
    description: row.description ?? null,
    audioLink: row.audio_link ?? null,
    photos: row.photos ?? [],
    /** Portada principal del anuncio (subida por el vendedor) para tarjetas del marketplace */
    coverUrl: row.cover_url ?? null,

    // tipo — itemFilters usa `type`, BD usa `format`
    type: row.format,

    // ubicación — tres columnas separadas para los filtros
    location: row.location ?? null,
    pais: row.pais ?? null,
    ciudad: row.ciudad ?? null,
    localidad: row.localidad ?? null,
    lat: (() => {
      const n = Number(row.lat);
      return Number.isFinite(n) ? n : null;
    })(),
    lng: (() => {
      const n = Number(row.lng);
      return Number.isFinite(n) ? n : null;
    })(),

    // subasta
    isAuction: row.is_auction ?? false,
    auctionStart: row.auction_start != null ? Number(row.auction_start) : null,
    auctionBuyNow: row.auction_buy_now != null ? Number(row.auction_buy_now) : null,
    auctionEndsAt: row.auction_ends_at ?? null,
    currentBid: null,
    bidCount: 0,

    // envío
    shipsDirect: row.ships_direct ?? true,
    shipsPickup: row.ships_pickup ?? false,
    shipsVerified: row.ships_verified ?? false,
    shipsPhysical: row.ships_physical ?? false,

    // edición especial
    esEdicionEspecial: row.es_edicion_especial ?? false,
    edicionNombre: row.edicion_nombre ?? null,
    edicionCatalogo: row.edicion_catalogo ?? null,
    edicionContenido: row.edicion_contenido ?? null,
    edicionNumerada: row.edicion_numerada ?? false,
    edicionNumeroEjemplar: row.edicion_numero_ejemplar ?? null,

    // conjunto
    esConjunto: row.es_conjunto ?? false,
    conjuntoTitulo: row.conjunto_titulo ?? null,
    conjuntoNumDiscos: row.conjunto_num_discos ?? null,
    conjuntoDescripcion: row.conjunto_descripcion ?? null,

    // meta
    status: row.status,
    views: row.views ?? 0,
    createdAt: row.created_at,
    featuredUntil: row.featured_until ?? null,
  };
}

/**
 * Carga todos los listings activos de Supabase con join al perfil del vendedor.
 * RLS ya filtra `status = 'active'` para usuarios no autenticados.
 * @returns {{ data: Array, error: string|null }}
 */
export async function fetchListings() {
  const { data, error } = await supabase
    .from('listings')
    .select(
      `
      *,
      profiles (
        username,
        avatar_url,
        is_verified,
        is_store,
        partner_active_roles
      )
    `
    )
    .eq('status', 'active')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[listingsService] Error fetching listings:', error.message);
    return { data: [], error: error.message };
  }

  const mapped = (data ?? []).map(mapListing);
  const enriched = await enrichListingsWithAuctionBids(mapped);
  const now = Date.now();
  enriched.sort((a, b) => {
    const af = a.featuredUntil && new Date(a.featuredUntil).getTime() > now ? 1 : 0;
    const bf = b.featuredUntil && new Date(b.featuredUntil).getTime() > now ? 1 : 0;
    if (af !== bf) return bf - af;
    const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return cb - ca;
  });
  return { data: enriched, error: null };
}

const LISTING_PUBLIC_SELECT = `
  *,
  profiles (
    username,
    avatar_url,
    is_verified,
    is_store,
    partner_active_roles
  )
`;

const LISTING_VIEW_DEBOUNCE_MS = 30 * 60 * 1000;

/**
 * Registra una visita al abrir ficha (debounce 30 min por anuncio en sessionStorage).
 * No cuenta visitas del propio vendedor (servidor).
 */
export async function recordListingView(listingId, { sellerUserId, viewerUserId } = {}) {
  const id = String(listingId ?? '').trim();
  if (!id) return;
  if (sellerUserId && viewerUserId && sellerUserId === viewerUserId) return;
  if (typeof window !== 'undefined') {
    const key = `dv_listing_view_${id}`;
    const last = Number(sessionStorage.getItem(key) || 0);
    if (last && Date.now() - last < LISTING_VIEW_DEBOUNCE_MS) return;
    sessionStorage.setItem(key, String(Date.now()));
  }
  const { error } = await supabase.rpc('increment_listing_view', { p_listing_id: id });
  if (error && process.env.NODE_ENV === 'development') {
    console.warn('[listingsService] recordListingView:', error.message);
  }
}

/**
 * Un listing público por id (deep link `?listing=`). Solo filas visibles por RLS (p. ej. `active`).
 * @returns {{ data: object|null, error: string|null }}
 */
export async function fetchPublicListingById(listingId) {
  const id = String(listingId ?? '').trim();
  if (!id) return { data: null, error: 'missing_id' };
  const { data, error } = await supabase
    .from('listings')
    .select(LISTING_PUBLIC_SELECT)
    .eq('id', id)
    .maybeSingle();
  if (error) {
    console.error('[listingsService] fetchPublicListingById:', error.message);
    return { data: null, error: error.message };
  }
  if (!data) return { data: null, error: null };
  const mapped = mapListing(data);
  const [enriched] = await enrichListingsWithAuctionBids([mapped]);
  return { data: enriched ?? mapped, error: null };
}

/**
 * Listings del vendedor para gestión (incluye pausados, reservados, retirados). RLS: solo el propio seller_id.
 * @param {string} sellerId — profiles.id / auth.uid()
 * @param {{ statuses?: string[] }} [options]
 */
export async function fetchSellerListingsForManage(sellerId, options = {}) {
  if (!sellerId) return { data: [], error: 'missing_seller' };
  const statuses = options.statuses?.length ? options.statuses : ['active', 'paused', 'reserved', 'removed'];
  const { data, error } = await supabase
    .from('listings')
    .select(
      `
      *,
      profiles (
        username,
        avatar_url,
        is_verified,
        is_store,
        partner_active_roles
      )
    `
    )
    .eq('seller_id', sellerId)
    .in('status', statuses)
    .order('created_at', { ascending: false })
    .limit(250);

  if (error) {
    console.error('[listingsService] fetchSellerListingsForManage:', error.message);
    return { data: [], error: error.message };
  }
  return { data: (data ?? []).map(mapListing), error: null };
}

/**
 * Cambia estado del listing si eres el vendedor (`active` | `paused` | `removed`).
 * @returns {{ error: string|null }}
 */
export async function updateSellerListingStatus(sellerId, listingId, status) {
  if (!sellerId || !listingId) return { error: 'Parámetros incompletos' };
  if (!['active', 'paused', 'removed'].includes(status)) return { error: 'Estado no permitido' };
  const { error } = await supabase.from('listings').update({ status }).eq('id', listingId).eq('seller_id', sellerId);
  if (error) {
    console.error('[listingsService] updateSellerListingStatus:', error.message);
    return { error: error.message };
  }
  return { error: null };
}

/**
 * Carga un listing del vendedor para edición (incluye pausados y reservados).
 */
export async function fetchSellerListingRowForEdit(sellerId, listingId) {
  if (!sellerId || !listingId) return { data: null, error: 'Parámetros incompletos' };
  const { data, error } = await supabase
    .from('listings')
    .select('*')
    .eq('id', listingId)
    .eq('seller_id', sellerId)
    .maybeSingle();
  if (error) {
    console.error('[listingsService] fetchSellerListingRowForEdit:', error.message);
    return { data: null, error: error.message };
  }
  if (!data) return { data: null, error: 'Anuncio no encontrado.' };
  return { data, error: null };
}

/**
 * Actualiza campos editables del listing (RLS: solo el vendedor).
 * @returns {{ error: string|null }}
 */
export async function updateSellerListing(sellerId, listingId, patch) {
  if (!sellerId || !listingId) return { error: 'Parámetros incompletos' };
  const { error } = await supabase
    .from('listings')
    .update(patch)
    .eq('id', listingId)
    .eq('seller_id', sellerId);
  if (error) {
    console.error('[listingsService] updateSellerListing:', error.message);
    return { error: error.message };
  }
  return { error: null };
}
